import type { ECharts } from 'echarts';
import {
  EVENT_AXIS_INDEX,
  EVENT_AXIS_LABEL_HEIGHT,
  EVENT_AXIS_LABEL_MARGIN,
  EVENT_AXIS_LABEL_WIDTH,
} from './buildDualEventXAxis';
import {
  getOpinionChartSeriesIndex,
  resolveOpinionXAxisDataIndex,
} from './syncOpinionChartDateAxisPointer';
import {
  OPINION_EVENT_FLOATER_WIDTH,
  type IOpinionEventFloaterAnchor,
} from '../useOpinionEventFloaterPosition';

export interface IEventAxisLabelAnchorFallback {
  offsetX: number;
  offsetY: number;
}

function calcEventAxisLabelTop(axisLineY: number): number {
  return axisLineY + EVENT_AXIS_LABEL_MARGIN;
}

function calcEventAxisLabelAnchor(
  axisLinePixel: { x: number; y: number },
  chartRect: DOMRect,
): IOpinionEventFloaterAnchor {
  return {
    left:
      chartRect.left +
      axisLinePixel.x -
      OPINION_EVENT_FLOATER_WIDTH / 2,
    top: chartRect.top + calcEventAxisLabelTop(axisLinePixel.y),
    width: EVENT_AXIS_LABEL_WIDTH,
    height: EVENT_AXIS_LABEL_HEIGHT,
  };
}

function tryConvertToPixel(
  chart: ECharts,
  finder: object,
  value: unknown,
): number[] | null {
  try {
    const pixel = chart.convertToPixel(finder, value) as number[] | undefined;
    if (
      Array.isArray(pixel) &&
      pixel.length >= 2 &&
      Number.isFinite(pixel[0]) &&
      Number.isFinite(pixel[1])
    ) {
      return pixel;
    }
  } catch {
    // ignore
  }
  return null;
}

function resolveEventAxisPixel(
  chart: ECharts,
  categoryValue: string,
  xAxisData: string[],
): number[] | null {
  const dataIndex = resolveOpinionXAxisDataIndex(
    chart,
    categoryValue,
    xAxisData,
    EVENT_AXIS_INDEX,
  );

  if (dataIndex >= 0) {
    const axisPixel =
      tryConvertToPixel(chart, { xAxisIndex: EVENT_AXIS_INDEX }, dataIndex) ||
      tryConvertToPixel(chart, { xAxisIndex: EVENT_AXIS_INDEX }, [
        categoryValue,
        0,
      ]);

    if (axisPixel) {
      return axisPixel;
    }

    const seriesIndex = getOpinionChartSeriesIndex(chart);
    const seriesPixel = tryConvertToPixel(chart, { seriesIndex }, [
      dataIndex,
      0,
    ]);
    if (seriesPixel) {
      const eventAxisY = tryConvertToPixel(
        chart,
        { xAxisIndex: EVENT_AXIS_INDEX },
        dataIndex,
      );
      if (eventAxisY) {
        return [seriesPixel[0], eventAxisY[1]];
      }
    }
  }

  return tryConvertToPixel(
    chart,
    { xAxisIndex: EVENT_AXIS_INDEX },
    categoryValue,
  );
}

function buildFallbackAnchor(
  chartRect: DOMRect,
  fallback: IEventAxisLabelAnchorFallback,
): IOpinionEventFloaterAnchor {
  return {
    left:
      chartRect.left +
      fallback.offsetX -
      OPINION_EVENT_FLOATER_WIDTH / 2,
    top: chartRect.top + fallback.offsetY - EVENT_AXIS_LABEL_HEIGHT,
    width: EVENT_AXIS_LABEL_WIDTH,
    height: EVENT_AXIS_LABEL_HEIGHT,
  };
}

export function getEventAxisLabelAnchor(
  chart: ECharts,
  categoryValue: string,
  xAxisData: string[],
  fallback?: IEventAxisLabelAnchorFallback,
): IOpinionEventFloaterAnchor | null {
  const chartRect = chart.getDom().getBoundingClientRect();
  const pixel = resolveEventAxisPixel(chart, categoryValue, xAxisData);

  if (pixel) {
    return calcEventAxisLabelAnchor(
      { x: pixel[0], y: pixel[1] },
      chartRect,
    );
  }

  return fallback ? buildFallbackAnchor(chartRect, fallback) : null;
}

// 供单测使用的计算函数
export { calcEventAxisLabelTop, calcEventAxisLabelAnchor };
